/* ==========================================================================
   NeuroSockz — Collection filtering and sorting

   Progressive enhancement over a plain GET form. Without JavaScript the form
   still submits and filters work; with JavaScript we swap the grid in place.
   ========================================================================== */
(function () {
  'use strict';

  // Guarded so that loading this file twice (e.g. a section referenced from two
  // templates) can never throw "the name has already been used".
  const defineOnce = (name, ctor) => {
    if (!customElements.get(name)) customElements.define(name, ctor);
  };

  class NeuroFacets extends HTMLElement {
    connectedCallback() {
      this.form = this.querySelector('form');
      this.sectionId = this.dataset.section;
      this.target = document.getElementById('ProductGridContainer');
      if (!this.form || !this.target) return;

      // The no-JS submit button is unnecessary once we can auto-apply.
      this.querySelectorAll('[data-facets-submit]').forEach((el) => el.classList.add('hidden'));

      this.onInput = debounceLocal(() => this.apply(), 320);
      this.form.addEventListener('input', (event) => {
        if (event.target.type === 'text' || event.target.type === 'number') this.onInput();
      });
      this.form.addEventListener('change', (event) => {
        if (event.target.type === 'text' || event.target.type === 'number') return;
        this.apply();
      });

      this.form.addEventListener('submit', (event) => {
        event.preventDefault();
        this.apply();
      });

      // Back/forward should restore the previous filtered view.
      window.addEventListener('popstate', () => this.apply(false));
    }

    buildParams() {
      const data = new FormData(this.form);
      const params = new URLSearchParams();
      for (const [key, value] of data.entries()) {
        if (value !== '') params.append(key, value);
      }
      return params.toString();
    }

    apply(pushUrl = true) {
      const params = pushUrl ? this.buildParams() : window.location.search.replace(/^\?/, '');
      const url = `${window.location.pathname}?section_id=${encodeURIComponent(this.sectionId)}${
        params ? `&${params}` : ''
      }`;

      this.target.classList.add('is-loading');
      this.setBusy(true);

      fetch(url)
        .then((response) => (response.ok ? response.text() : Promise.reject(response.status)))
        .then((text) => {
          const html = new DOMParser().parseFromString(text, 'text/html');
          const fresh = html.getElementById('ProductGridContainer');
          if (fresh) this.target.innerHTML = fresh.innerHTML;

          const freshFilters = html.querySelector('[data-facets-filters]');
          const currentFilters = this.querySelector('[data-facets-filters]');
          if (freshFilters && currentFilters) currentFilters.innerHTML = freshFilters.innerHTML;

          if (pushUrl) {
            const newUrl = `${window.location.pathname}${params ? `?${params}` : ''}`;
            window.history.pushState({ params }, '', newUrl);
          }

          // Announce the new result count to screen readers.
          const count = html.getElementById('ProductCount');
          const currentCount = document.getElementById('ProductCount');
          if (count && currentCount) {
            currentCount.textContent = count.textContent;
            currentCount.dataset.productCount = count.dataset.productCount;
          }
          const live = document.getElementById('FacetsLiveRegion');
          if (count && live) live.textContent = count.textContent.trim();
        })
        .catch(() => {
          // If the swap fails, fall back to a full navigation rather than
          // leaving the shopper looking at stale results.
          window.location.search = params;
        })
        .finally(() => {
          this.target.classList.remove('is-loading');
          this.setBusy(false);
        });
    }

    setBusy(state) {
      this.target.setAttribute('aria-busy', String(state));
    }
  }

  // Local debounce so this file has no load-order dependency on global.js.
  function debounceLocal(fn, wait) {
    let timer;
    return function debounced(...args) {
      clearTimeout(timer);
      timer = setTimeout(() => fn.apply(this, args), wait);
    };
  }

  defineOnce('neuro-facets', NeuroFacets);
})();

